import { Injectable, inject } from '@angular/core';
import { BaseRequester } from '../common/base-requester';
import { mergeMap, map, reduce, switchMap } from 'rxjs/operators';
import { AjaxResponse } from 'rxjs/ajax';
import {
    get as _get,
    map as _map,
    set as _set,
    concat as _concat,
    chunk as _chunk,
    isEmpty as _isEmpty,
    find as _find,
} from 'lodash';
import { forkJoin, from, Observable, of, Subject, throwError } from 'rxjs';
import { OrderStatus } from './enums/order-status-v2';
import {
    ShopeeDelegateRequest,
    SsmAuthInfo,
} from './interfaces/shopee-delegate-request';
import { environment } from '../../../../environments/environment';
import { Moment } from 'moment';
import { ShopeeShippingDocDataInfo } from './interfaces/shopee-shipping-doc-data';
import { unwrapLsiEnvelope } from '../common/lsi-envelope';
import {
    FINANCE_FETCH_CONCURRENCY,
    retryOnThrottle,
} from '../common/throttle-retry';

@Injectable({
    providedIn: 'root',
})
export class ShopeeOpenServiceV2 extends BaseRequester {
    private ssmAuthInfo = inject<SsmAuthInfo>('ssmAuthInfo' as any);

    private delegateEndpoint = environment.shopeeDelegateV2EndPoint;

    private ORDER_DETAIL_FIELDS = [
        'buyer_user_id',
        'buyer_username',
        'estimated_shipping_fee',
        'recipient_address',
        'actual_shipping_fee',
        'goods_to_declare',
        'note',
        'note_update_time',
        'item_list',
        'pay_time',
        'dropshipper',
        'dropshipper_phone',
        'split_up',
        'buyer_cancel_reason',
        'cancel_by',
        'cancel_reason',
        'actual_shipping_fee_confirmed',
        'buyer_cpf_id',
        'fulfillment_flag',
        'pickup_done_time',
        'package_list',
        'shipping_carrier',
        'payment_method',
        'total_amount',
        'buyer_username',
        'invoice_data',
        'checkout_shipping_carrier',
        'reverse_shipping_fee',
        'order_chargeable_weight_gram',
        'edt',
        'prescription_images',
        'prescription_check_status',
    ];

    // Orders CREATED inside the window (at most 15 days).
    getOrderSnsByStatus(
        timeFrom: Moment,
        timeTo: Moment,
        orderStatus?: OrderStatus,
    ): Observable<any[]> {
        return this.listOrderSns('create_time', timeFrom, timeTo, orderStatus);
    }

    // Orders last UPDATED inside the window (same 15-day cap) — for a change
    // that comes long after the order was placed, like a failed delivery's
    // cancellation.
    getOrderSnsUpdatedBetween(
        timeFrom: Moment,
        timeTo: Moment,
        orderStatus?: OrderStatus,
    ): Observable<any[]> {
        return this.listOrderSns('update_time', timeFrom, timeTo, orderStatus);
    }

    private listOrderSns(
        timeRangeField: 'create_time' | 'update_time',
        timeFrom: Moment,
        timeTo: Moment,
        orderStatus?: OrderStatus,
        cursor?: string,
        accumItem?: any[],
    ): Observable<any[]> {
        const request: ShopeeDelegateRequest = {
            authInfo: this.ssmAuthInfo,
            servicePath: 'order/get_order_list',
            parameter: {
                time_range_field: timeRangeField,
                time_from: timeFrom.unix(),
                time_to: timeTo.unix(),
                page_size: 100,
                response_optional_fields: 'order_status',
            },
        };
        if (orderStatus) {
            _set(request.parameter, 'order_status', orderStatus);
        }
        if (cursor) {
            _set(request.parameter, 'cursor', cursor);
        }
        return this.requestShopeeDelegate(request).pipe(
            mergeMap((body: any) => {
                ShopeeOpenServiceV2.throwOnShopeeError(body);
                const orderList = _get(body, 'response.order_list');
                const orderItems = _map(orderList, (order) => {
                    return {
                        orderSn: order['order_sn'],
                        orderStatus: order['order_status'],
                    };
                });
                if (!accumItem) {
                    accumItem = [];
                }
                accumItem = _concat(accumItem, orderItems);
                const hasMore: boolean = _get(body, 'response.more');
                if (hasMore && orderItems.length > 0) {
                    const nextCursor = _get(body, 'response.next_cursor');
                    return this.listOrderSns(
                        timeRangeField,
                        timeFrom,
                        timeTo,
                        orderStatus,
                        nextCursor,
                        accumItem,
                    );
                } else {
                    return of(accumItem);
                }
            }),
        );
    }

    getOrderDetails(orderSn: string[]): Observable<any[]> {
        const orderSnChunks: string[][] = _chunk(orderSn, 50);
        return from(orderSnChunks).pipe(
            // Bounded, and each chunk retried on throttle. Unbounded, a
            // 500-order month fired eleven chunks at once next to the escrow
            // fan-out, Shopee answered some with error_rate_limit, and — with
            // no error check below — those chunks became empty pages: orders
            // stored with no create_time, hence no tax point.
            mergeMap((orderSnChunk: string[]) => {
                const request: ShopeeDelegateRequest = {
                    authInfo: this.ssmAuthInfo,
                    servicePath: 'order/get_order_detail',
                    parameter: {
                        order_sn_list: `${orderSnChunk.toString()}`,
                        response_optional_fields: `${this.ORDER_DETAIL_FIELDS.toString()}`,
                    },
                };
                return this.requestShopeeDelegate(request).pipe(
                    retryOnThrottle(),
                    map((body: any) => {
                        ShopeeOpenServiceV2.throwOnShopeeError(body);
                        // Default to [] so a chunk without order_list doesn't
                        // concat an undefined entry into the merged results.
                        const orderDetails: any[] =
                            _get(body, 'response.order_list') || [];
                        // A short page with no `error` is Shopee's quietest
                        // failure: on 2026-10-23 one chunk of 50 came back
                        // empty and the finance sync wrote 50 orders with no
                        // tax point. Logged with the body's shape so the
                        // next occurrence can be read; the finance sync
                        // refetches the misses itself.
                        if (orderDetails.length < orderSnChunk.length) {
                            console.warn(
                                `order/get_order_detail returned ${orderDetails.length} of ${orderSnChunk.length}`,
                                Object.keys(body ?? {}),
                                _get(body, 'warning') ?? _get(body, 'message'),
                            );
                        }
                        return orderDetails;
                    }),
                );
            }, FINANCE_FETCH_CONCURRENCY),
        );
    }

    getOrderByStatusIncludingDetails(
        timeFrom: Moment,
        timeTo: Moment,
        orderStatus?: OrderStatus,
    ): Subject<any[]> {
        const result = new Subject<any[]>();
        let orderDetails: any[] = [];
        this.getOrderSnsByStatus(timeFrom, timeTo, orderStatus)
            .pipe(
                mergeMap((orderSnObjList: any[]) => {
                    return this.getOrderDetails(
                        _map(orderSnObjList, 'orderSn'),
                    );
                }),
            )
            .subscribe(
                (orderDetailResults: any[]) => {
                    orderDetails = _concat(orderDetails, orderDetailResults);
                },
                (err) => {
                    result.error(err);
                },
                () => {
                    result.next(orderDetails);
                    result.complete();
                },
            );
        return result;
    }

    getShippingParam(orderSn: string): Observable<any> {
        const request: ShopeeDelegateRequest = {
            authInfo: this.ssmAuthInfo,
            servicePath: 'logistics/get_shipping_parameter',
            parameter: {
                order_sn: orderSn,
            },
        };
        return this.requestShopeeDelegate(request).pipe(
            switchMap((body: any) => {
                const error = _get(body, 'error');
                if (error) {
                    return throwError(_get(body, 'message'));
                }
                return of(_get(body, 'response'));
            }),
        );
    }

    getTrackingNumbers(orderSnList: string[]): Observable<any[]> {
        if (_isEmpty(orderSnList)) {
            return of([]);
        }
        const getTrackingNumberObsList = _map(
            orderSnList,
            (orderSn: string) => {
                const request: ShopeeDelegateRequest = {
                    authInfo: this.ssmAuthInfo,
                    servicePath: 'logistics/get_tracking_number',
                    parameter: {
                        order_sn: orderSn,
                    },
                };
                return this.requestShopeeDelegate(request).pipe(
                    map((body: any) => {
                        const trackingNumber: string = _get(
                            body,
                            'response.tracking_number',
                        );
                        return {
                            orderSn: orderSn,
                            trackingNumber: trackingNumber,
                        };
                    }),
                );
            },
        );
        return forkJoin(getTrackingNumberObsList);
    }

    // The courier's event history for an order's parcel, as
    // [{ update_time, logistics_status, description }]. The only place a
    // failed delivery's arrival back at the shop shows up (RETURNED): the
    // order itself stops changing when the courier turns it around.
    getTrackingInfo(orderSn: string): Observable<any[]> {
        const request: ShopeeDelegateRequest = {
            authInfo: this.ssmAuthInfo,
            servicePath: 'logistics/get_tracking_info',
            parameter: { order_sn: orderSn },
        };
        return this.requestShopeeDelegate(request).pipe(
            retryOnThrottle(),
            map((body: any) => {
                ShopeeOpenServiceV2.throwOnShopeeError(body);
                return _get(body, 'response.tracking_info') || [];
            }),
        );
    }

    shipOrder(
        orderSn: string,
        addressId: number,
        pickupTimeId: string,
    ): Observable<string> {
        const request: ShopeeDelegateRequest = {
            authInfo: this.ssmAuthInfo,
            servicePath: 'logistics/ship_order',
            method: 'post',
            parameter: {
                order_sn: orderSn,
                pickup: {
                    address_id: addressId,
                    pickup_time_id: pickupTimeId,
                },
            },
        };
        return this.requestShopeeDelegate(request).pipe(
            map((body: any): string => {
                const error = _get(body, 'error');
                if (error) {
                    throw new Error(`Error: ${error} ${_get(body, 'message')}`);
                }
                return _get(body, 'request_id');
            }),
        );
    }

    shipOrderDropoff(orderSn: string, branchId?: number): Observable<string> {
        const dropoff: { branch_id?: number } = {}; // eslint-disable-line @typescript-eslint/naming-convention
        if (branchId) {
            dropoff.branch_id = branchId;
        }
        const request: ShopeeDelegateRequest = {
            authInfo: this.ssmAuthInfo,
            servicePath: 'logistics/ship_order',
            method: 'post',
            parameter: {
                order_sn: orderSn,
                dropoff,
            },
        };
        return this.requestShopeeDelegate(request).pipe(
            map((body: any): string => {
                const error = _get(body, 'error');
                if (error) {
                    throw new Error(`Error: ${error} ${_get(body, 'message')}`);
                }
                return _get(body, 'request_id');
            }),
        );
    }

    getShippingDocInfo(
        orderSn: string,
        recipientNameWidth: number,
        recipientAddrWidth: number,
        recipientAddrFontSize: number | null = null,
    ): Observable<ShopeeShippingDocDataInfo> {
        const request: ShopeeDelegateRequest = {
            authInfo: this.ssmAuthInfo,
            servicePath: 'logistics/get_shipping_document_data_info',
            method: 'post',
            parameter: {
                order_sn: orderSn,
                recipient_address_info: [
                    {
                        key: 'name',
                        style: {
                            image_width: recipientNameWidth,
                            h_align: 'left',
                        },
                    },
                    {
                        key: 'full_address',
                        style: {
                            image_width: recipientAddrWidth,
                            h_align: 'left',
                            ...(recipientAddrFontSize && {
                                font_size: recipientAddrFontSize,
                            }),
                        },
                    },
                ],
            },
        };
        return this.requestShopeeDelegate(request).pipe(
            map((body: any): ShopeeShippingDocDataInfo => {
                const error = _get(body, 'error');
                if (error) {
                    throw new Error(`Error: ${error} ${_get(body, 'message')}`);
                }
                const response = _get(body, 'response');
                const docInfo: ShopeeShippingDocDataInfo = {
                    orderSn: orderSn,
                    recipientNameBase64: null,
                    recipientFullAddrBase64: null,
                    firstRecipientSortCode: _get(
                        response,
                        'shipping_document_info.recipient_sort_code.first_recipient_sort_code',
                    ),
                };
                // navigator.clipboard
                //     .writeText(response.recipient_address_info[1].image)
                //     .then(
                //         () => {
                //             console.log('done');
                //         },
                //         (e) => {
                //             console.error(e);
                //         }
                //     );

                const recipientAddrInfo: { key: string; image: string }[] =
                    _get(response, 'recipient_address_info');
                if (recipientAddrInfo) {
                    const nameObj: { key: string; image: string } | undefined =
                        _find(recipientAddrInfo, {
                            key: 'name',
                        });
                    if (nameObj && nameObj.image) {
                        docInfo.recipientNameBase64 = nameObj.image;
                    }

                    const addrObj: { key: string; image: string } | undefined =
                        _find(recipientAddrInfo, {
                            key: 'full_address',
                        });
                    if (addrObj && addrObj.image) {
                        docInfo.recipientFullAddrBase64 = addrObj.image;
                    }
                }
                return docInfo;
            }),
        );
    }

    // --- Finance -----------------------------------------------------------
    //
    // Shopee splits the money across two endpoints: get_escrow_list says WHICH
    // orders were paid out and when, get_escrow_detail_batch says what each one
    // actually earned. Both are under the `payment/` namespace, not `order/`.

    // Orders whose escrow was released in the window, oldest page first.
    // Shopee caps the window at roughly 15 days, so a month needs three calls;
    // the caller is responsible for splitting the range.
    //
    // `escrow_release_time` here is the only settlement date Shopee gives —
    // the escrow detail payload has no timestamps at all.
    getEscrowList(
        releaseTimeFrom: Moment,
        releaseTimeTo: Moment,
        pageNo: number = 1,
        accumItem?: any[],
    ): Observable<any[]> {
        const request: ShopeeDelegateRequest = {
            authInfo: this.ssmAuthInfo,
            servicePath: 'payment/get_escrow_list',
            parameter: {
                release_time_from: releaseTimeFrom.unix(),
                release_time_to: releaseTimeTo.unix(),
                page_size: 100,
                page_no: pageNo,
            },
        };
        // Retry THIS page, not the whole sequence. Wrapping the recursive call
        // instead would restart pagination from page 1 on a late 429 and
        // re-issue every call that already succeeded — which adds load at
        // exactly the moment the platform is asking for less.
        return this.requestShopeeDelegate(request).pipe(
            retryOnThrottle(),
            mergeMap((body: any) => {
                ShopeeOpenServiceV2.throwOnShopeeError(body);
                const page: any[] = _get(body, 'response.escrow_list') || [];
                const accumulated = _concat(accumItem || [], page);
                // Guard on page length as well as `more`: a truthy `more` with
                // an empty page would otherwise recurse forever.
                const hasMore: boolean = _get(body, 'response.more');
                if (hasMore && page.length > 0) {
                    return this.getEscrowList(
                        releaseTimeFrom,
                        releaseTimeTo,
                        pageNo + 1,
                        accumulated,
                    );
                }
                return of(accumulated);
            }),
        );
    }

    // The full fee breakdown, up to 50 orders per call. POST, because the
    // order list goes in the body rather than the query string.
    getEscrowDetails(orderSn: string[]): Observable<any[]> {
        if (_isEmpty(orderSn)) {
            return of([]);
        }
        const chunks: string[][] = _chunk(orderSn, 50);
        return from(chunks).pipe(
            // Concurrency 2, not unbounded. A busy month is ~10 chunks, and
            // firing all of them at once alongside the order-detail fan-out is
            // what earns a 429 in the first place.
            mergeMap((chunk: string[]) => {
                const request: ShopeeDelegateRequest = {
                    authInfo: this.ssmAuthInfo,
                    servicePath: 'payment/get_escrow_detail_batch',
                    method: 'post',
                    parameter: { order_sn_list: chunk },
                };
                return this.requestShopeeDelegate(request).pipe(
                    retryOnThrottle(),
                    map((body: any) => {
                        ShopeeOpenServiceV2.throwOnShopeeError(body);
                        // Unlike the order endpoints, the batch response IS
                        // the array — each entry wrapping an `escrow_detail`.
                        const rows: any[] = _get(body, 'response') || [];
                        return rows
                            .map((row: any) => _get(row, 'escrow_detail'))
                            .filter(Boolean);
                    }),
                );
            }, FINANCE_FETCH_CONCURRENCY),
            // One emission with every chunk, not one per chunk. The finance
            // sync joins this with the order details inside a forkJoin, and
            // forkJoin keeps only the LAST value of each source — so per-chunk
            // emissions meant a 14-day window with 300 settled orders kept
            // the 50 from whichever chunk finished last, and the order-date
            // join missed whenever the two sources' last chunks differed.
            // That is what "47 รายการไม่มี tax point" was: one whole chunk.
            reduce((all: any[], rows: any[]) => all.concat(rows), [] as any[]),
        );
    }

    // --- Returns -----------------------------------------------------------

    // Returns created inside the window, oldest first, 100 per page. Shopee
    // caps the window at 15 days (error_param beyond that), so a longer
    // period is several calls — see the return-orders service.
    //
    // ⚠️ `page_no` is ZERO-based here, unlike get_escrow_list, where it
    // starts at 1. Asking for page 1 of a window that fits in one page
    // returns an empty list with no error, which on 2026-09-22 looked like
    // the time filter silently ignoring its arguments.
    getReturnList(
        createTimeFrom: Moment,
        createTimeTo: Moment,
        pageNo: number = 0,
        accumItem?: any[],
    ): Observable<any[]> {
        const request: ShopeeDelegateRequest = {
            authInfo: this.ssmAuthInfo,
            servicePath: 'returns/get_return_list',
            parameter: {
                page_no: pageNo,
                page_size: 100,
                create_time_from: createTimeFrom.unix(),
                create_time_to: createTimeTo.unix(),
            },
        };
        // Retry this page only — see getEscrowList.
        return this.requestShopeeDelegate(request).pipe(
            retryOnThrottle(),
            mergeMap((body: any) => {
                ShopeeOpenServiceV2.throwOnShopeeError(body);
                const page: any[] = _get(body, 'response.return') || [];
                const accumulated = _concat(accumItem || [], page);
                const hasMore: boolean = _get(body, 'response.more');
                if (hasMore && page.length > 0) {
                    return this.getReturnList(
                        createTimeFrom,
                        createTimeTo,
                        pageNo + 1,
                        accumulated,
                    );
                }
                return of(accumulated);
            }),
        );
    }

    // One return in full. The list omits `logistics_status` — the only field
    // that says whether the carrier has delivered the package back to the
    // shop — so the return tracker fetches this for each return it shows.
    getReturnDetail(returnSn: string): Observable<any> {
        const request: ShopeeDelegateRequest = {
            authInfo: this.ssmAuthInfo,
            servicePath: 'returns/get_return_detail',
            parameter: { return_sn: returnSn },
        };
        return this.requestShopeeDelegate(request).pipe(
            retryOnThrottle(),
            map((body: any) => {
                ShopeeOpenServiceV2.throwOnShopeeError(body);
                return _get(body, 'response') || {};
            }),
        );
    }

    // Shopee reports a failure as HTTP 200 with a non-empty top-level `error`
    // ("error_rate_limit", "error_auth", "error_param", …) and a `message`;
    // `response` is then empty. The finance calls used to look only at
    // `response.errorMessage`, which Shopee never sets, so every such failure
    // read as an empty page: a rate-limited escrow list silently dropped the
    // first half of August 2026 and Verify reported "platform: 0". Thrown as
    // `<error>: <message>` so isThrottleError can match the code and retry.
    private static throwOnShopeeError(body: any): void {
        const error = String(_get(body, 'error') ?? '').trim();
        if (error) {
            throw new Error(
                `${error}: ${String(_get(body, 'message') ?? '').trim()}`,
            );
        }
        const legacy = String(_get(body, 'response.errorMessage') ?? '');
        if (legacy) {
            throw new Error(legacy);
        }
    }

    private requestShopeeDelegate(
        request: ShopeeDelegateRequest,
    ): Observable<any> {
        return this.internalPostRequest<ShopeeDelegateRequest>(
            this.delegateEndpoint,
            request,
        ).pipe(
            map((ajaxRes: AjaxResponse) =>
                unwrapLsiEnvelope(_get(ajaxRes, 'response')),
            ),
        );
    }
}
